import PropTypes from 'prop-types';
import React from 'react';
import { motion } from 'motion/react';
import { Flex } from '../grid';
import { HeadingWithFlameHighlights } from '../heading-with-flame-highlights';
import { H2 } from '../typography/new';

const eyebrowAnimationTime = 0.2;
const delayFromEyebrow = 0;
const headingAnimationDelay = 0.4;
const wordAnimationTime = 0.4; // From heading-with-flame-highlights
const highlightTime = 0.4; // From heading-with-flame-highlights

export const calculateTotalAnimationTime = ({ delay = 0, headingCount = 3, averageWordsPerHeading = 3 }) => {
  // Total time calculation breakdown:
  // 1. Initial delay
  // 2. Eyebrow animation (0.2s)
  // 3. Delay from eyebrow (0s)
  // 4. Last heading delay = headingCount * headingAnimationDelay
  // 5. Word animations = averageWordsPerHeading * (wordAnimationTime / 2)
  // 6. Highlight animation duration = highlightTime

  const lastHeadingDelay = headingCount * headingAnimationDelay + delay + eyebrowAnimationTime + delayFromEyebrow;
  const wordAnimationsDuration = averageWordsPerHeading * (wordAnimationTime / 2);

  return lastHeadingDelay + wordAnimationsDuration + highlightTime;
};

const ThreeLineHighlight = ({
  heading,
  eyebrow,
  isAnimated = true,
  delay,
  headingLevel,
  eyebrowBackgroundColor,
  disableHighlightAnimation,
  fullTilt,
  eyebrowTextColor,
  lineHeight,
  lineNegativeMarginTop,
  animate,
  ...rest
}) => {
  const fullAnimationDelay = delay + delayFromEyebrow;

  const [animationStep, setAnimationStep] = React.useState(null);

  const animationState = animate || animationStep;

  return (
    <Flex width="100%" flexDirection="column" alignItems="center" justifyContent="center" {...rest}>
      <motion.div
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        viewport={{ amount: 0.3, once: true }}
        onViewportEnter={() => {
          setAnimationStep('visible');
        }}
      >
        <motion.div
          initial={isAnimated ? 'hidden' : 'visible'}
          animate={animationState}
          variants={{
            visible: {
              scale: 1,
              transition: {
                duration: eyebrowAnimationTime,
                delay,
                ease: [0.34, 1.56, 0.64, 1],
              },
            },
            hidden: {
              scale: 0,
            },
          }}
        >
          <HeadingWithFlameHighlights
            mb="0"
            isAnimated={false}
            variant="highlight-pill"
            heading={eyebrow}
            backgroundColor={eyebrowBackgroundColor}
            color={eyebrowTextColor}
            zIndex={15}
          />
        </motion.div>
        {heading.map((line, index) => (
          <HeadingWithFlameHighlights
            backgroundColor="black"
            color="white"
            heading={line}
            lineHeight={lineHeight}
            headingLevel={headingLevel}
            mt={index === 0 ? '0rem' : lineNegativeMarginTop}
            animationDelay={(index + 1) * headingAnimationDelay + fullAnimationDelay}
            disableHighlightAnimation={disableHighlightAnimation}
            mb="0"
            isAnimated={isAnimated}
            animate={animationState}
            animationIndex={index + 1}
            style={{
              transform: fullTilt || index % 2 === 0 ? 'rotate(-3deg)' : 'rotate(0deg)',
            }}
          />
        ))}
      </motion.div>
    </Flex>
  );
};

const threeLineHighlightPropTypes = {
  heading: PropTypes.arrayOf(PropTypes.string).isRequired,
  eyebrow: PropTypes.string,
  isAnimated: PropTypes.bool,
  delay: PropTypes.number,
  headingLevel: PropTypes.object,
  disableHighlightAnimation: PropTypes.bool,
  fullTilt: PropTypes.bool,
  eyebrowBackgroundColor: PropTypes.string,
  eyebrowTextColor: PropTypes.string,
  lineHeight: PropTypes.string,
  lineNegativeMarginTop: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  animate: PropTypes.oneOf(['hidden', 'visible', 'onscreen']),
};

const threeLineHighlightDefaultProps = {
  eyebrow: null,
  isAnimated: true,
  delay: 0,
  headingLevel: H2,
  disableHighlightAnimation: false,
  fullTilt: false,
  eyebrowBackgroundColor: 'black',
  eyebrowTextColor: 'white',
  lineHeight: '1.2em',
  lineNegativeMarginTop: '0rem',
  animate: null,
};

ThreeLineHighlight.propTypes = threeLineHighlightPropTypes;

ThreeLineHighlight.defaultProps = threeLineHighlightDefaultProps;

export { ThreeLineHighlight, threeLineHighlightPropTypes, threeLineHighlightDefaultProps };
